/**
 * The delegations of each Tunisian governorate (one level below `GOVERNORATES`), so a buyer picks their delegation
 * from the list of the governorate they chose instead of typing it — no misspelt or mismatched delivery address.
 *
 * Source: Wikipedia, « Delegations of Tunisia » (revision of March 2026), which lists them per governorate from the
 * Institut National de la Statistique's 2024 census. Keyed by the `Governorate.code` of `governorates.ts`; names in
 * their usual Latin spelling (also what the carrier's address field receives).
 */
export const DELEGATIONS: Readonly<Record<string, readonly string[]>> = {
  TUN: [
    'Bab El Bhar',
    'Bab Souika',
    'Carthage',
    'Cité El Khadra',
    'Djebel Jelloud',
    'El Hraïria',
    'El Kabaria',
    'El Menzah',
    'El Omrane',
    'El Omrane Supérieur',
    'El Ouardia',
    'Ettahrir',
    'Ezzouhour',
    'La Goulette',
    'La Marsa',
    'Le Bardo',
    'Le Kram',
    'Medina',
    'Séjoumi',
    'Sidi El Béchir',
    'Sidi Hassine',
  ],
  ARI: [
    'Ariana Ville',
    'Cité Ettadhamen',
    'Kalâat El Andalous',
    'La Soukra',
    'Mnihla',
    'Raoued',
    'Sidi Thabet',
  ],
  BEN: [
    'Ben Arous',
    'Bou Mhel El Bassatine',
    'El Mourouj',
    'Ezzahra',
    'Fouchana',
    'Hammam Chott',
    'Hammam Lif',
    'Medina Jedida',
    'Mégrine',
    'Mohamedia',
    'Mornag',
    'Radès',
  ],
  MAN: [
    'Borj El Amri',
    'Djedeida',
    'Douar Hicher',
    'El Batan',
    'Manouba',
    'Mornaguia',
    'Oued Ellil',
    'Tebourba',
  ],
  NAB: [
    'Béni Khalled',
    'Béni Khiar',
    'Bou Argoub',
    'Dar Châabane El Fehri',
    'El Haouaria',
    'El Mida',
    'Grombalia',
    'Hammamet',
    'Hammam Ghezèze',
    'Kélibia',
    'Korba',
    'Menzel Bouzelfa',
    'Menzel Temime',
    'Nabeul',
    'Soliman',
    'Takelsa',
  ],
  ZAG: ['Bir Mcherga', 'El Fahs', 'Nadhour', 'Saouaf', 'Zaghouan', 'Zriba'],
  BIZ: [
    'Bizerte Nord',
    'Bizerte Sud',
    'El Alia',
    'Ghar El Melh',
    'Ghezala',
    'Joumine',
    'Mateur',
    'Menzel Bourguiba',
    'Menzel Jemil',
    'Ras Jebel',
    'Sejnane',
    'Tinja',
    'Utique',
    'Zarzouna',
  ],
  BEJ: [
    'Amdoun',
    'Béja Nord',
    'Béja Sud',
    'Goubellat',
    'Medjez El Bab',
    'Nefza',
    'Téboursouk',
    'Testour',
    'Thibar',
  ],
  JEN: [
    'Aïn Draham',
    'Balta - Bou Aouane',
    'Bou Salem',
    'Fernana',
    'Ghardimaou',
    'Jendouba',
    'Jendouba Nord',
    'Oued Meliz',
    'Tabarka',
  ],
  KEF: [
    'Dahmani',
    'El Ksour',
    'Jérissa',
    'Kalâat Khasba',
    'Kalaat Senan',
    'Kef Est',
    'Kef Ouest',
    'Nebeur',
    'Sakiet Sidi Youssef',
    'Sers',
    'Tajerouine',
    'Touiref',
  ],
  SIL: [
    'Bargou',
    'Bou Arada',
    'El Aroussa',
    'El Krib',
    'Gaâfour',
    'Kesra',
    'Makthar',
    'Rouhia',
    'Sidi Bou Rouis',
    'Siliana Nord',
    'Siliana Sud',
  ],
  KAI: [
    'Aïn Djeloula',
    'Bou Hajla',
    'Chebika',
    'Echrarda',
    'El Alâa',
    'Haffouz',
    'Hajeb el Ayoun',
    'Kairouan Nord',
    'Kairouan Sud',
    'Menzel Mehiri',
    'Nasrallah',
    'Oueslatia',
    'Sbikha',
  ],
  KAS: [
    'El Ayoun',
    'Ezzouhour',
    'Fériana',
    'Foussana',
    'Haïdra',
    'Hassi El Ferid',
    'Jedelienne',
    'Kasserine Nord',
    'Kasserine Sud',
    'Majel Bel Abbès',
    'Sbeïtla',
    'Sbiba',
    'Thala',
  ],
  SBZ: [
    'Bir El Hafey',
    'Cebbala Ouled Asker',
    'El Hichria',
    'Essaïda',
    'Jilma',
    'Meknassy',
    'Menzel Bouzaiane',
    'Mezzouna',
    'Ouled Haffouz',
    'Regueb',
    'Sidi Ali Ben Aoun',
    'Sidi Bouzid Est',
    'Sidi Bouzid Ouest',
    'Souk Jedid',
  ],
  SOU: [
    'Akouda',
    'Bouficha',
    'Enfida',
    'Hammam Sousse',
    'Hergla',
    'Kalâa Kebira',
    'Kalâa Seghira',
    'Kondar',
    "M'saken",
    'Sidi Bou Ali',
    'Sidi El Hani',
    'Sousse Jawhara',
    'Sousse Médina',
    'Sousse Riadh',
    'Sousse Sidi Abdelhamid',
    'Zaouiet - Ksibet Thrayet',
  ],
  MON: [
    'Bekalta',
    'Bembla',
    'Beni Hassen',
    'Jemmal',
    'Ksar Hellal',
    'Ksibet El Médiouni',
    'Moknine',
    'Monastir',
    'Ouerdanine',
    'Sahline',
    'Sayada - Lamta - Bouhjar',
    'Téboulba',
    'Zéramdine',
  ],
  MAH: [
    'Bou Merdes',
    'Chebba',
    'Chorbane',
    'El Bradâa',
    'El Jem',
    'Essouassi',
    'Hebira',
    'Ksour Essef',
    'Mahdia',
    'Melloulèche',
    'Ouled Chamekh',
    'Rejiche',
    'Sidi Alouane',
  ],
  SFA: [
    'Agareb',
    'Bir Ali Ben Khalifa',
    'El Amra',
    'El Hencha',
    'Graïba',
    'Jebiniana',
    'Kerkennah',
    'Mahrès',
    'Menzel Chaker',
    'Sakiet Eddaïer',
    'Sakiet Ezzit',
    'Sfax Ouest',
    'Sfax Sud',
    'Sfax Ville',
    'Skhira',
    'Thyna',
  ],
  GAF: [
    'Belkhir',
    'El Guettar',
    'El Ksar',
    'Gafsa Nord',
    'Gafsa Sud',
    'Mdhila',
    'Métlaoui',
    'Moularès',
    'Redeyef',
    'Sened',
    'Sidi Aïch',
    'Sidi Boubaker',
    'Zannouch',
  ],
  TOZ: ['Degache', 'El Hamma du Jérid', 'Hazoua', 'Nefta', 'Tamerza', 'Tozeur'],
  KEB: [
    'Douz Nord',
    'Douz Sud',
    'Faouar',
    'Kébili Nord',
    'Kébili Sud',
    'Rjim Maatoug',
    'Souk Lahad',
  ],
  GAB: [
    'Dkhilet Toujane',
    'El Hamma',
    'Gabès Médina',
    'Gabès Ouest',
    'Gabès Sud',
    'Ghannouch',
    'Habib Thameur Bouatouch',
    'Mareth',
    'Matmata',
    'Menzel El Habib',
    'Métouia',
    'Nouvelle Matmata',
    'Oudhref',
  ],
  MED: [
    'Ben Gardane',
    'Beni Khedache',
    'Djerba Ajim',
    'Djerba Houmt Souk',
    'Djerba Midoun',
    'Médenine Nord',
    'Médenine Sud',
    'Sidi Makhlouf',
    'Zarzis',
  ],
  TAT: [
    'Beni Mehira',
    'Bir Lahmar',
    'Dehiba',
    'Ghomrassen',
    'Remada',
    'Smâr',
    'Tataouine Nord',
    'Tataouine Sud',
  ],
};

/** The delegations of a governorate (by code), or none for an unknown code. */
export function delegationsOf(governorateCode: string): readonly string[] {
  return DELEGATIONS[governorateCode] ?? [];
}

export function isDelegationOf(governorateCode: string, delegation: string): boolean {
  return delegationsOf(governorateCode).includes(delegation);
}
